import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useMeetingState } from "@daily-co/daily-react";
import { useAppDispatch } from "store/hooks";
import { Actions, trackActionEnd, trackActionStart } from "modules/monitoring";
import logger from "logging/logger";
import { useDevices } from "modules/audioVideo/hooks";
import {
  selectAudioPermissionsState,
  selectVideoPermissionsState,
} from "modules/deviceInfo";
import { DevicePromptPopup } from "modules/audioVideo/components";
import {
  selectAdBlockResult,
  selectFirebaseResult,
  selectFirebaseStorageResult,
  selectFirestoreResult,
  selectInternetSpeedResult,
  selectIsLoading,
  selectIsRequiredLoading,
  selectReachabilityResult,
  selectRequiredSystemStatus,
  selectSystemInfo,
  selectSystemStatus,
  selectVPNDetectionResult,
  selectDisableVPNCheck,
  selectDailyResult,
  selectHaveChecksBeenRun,
} from "../../redux/selectors";
import {
  setAllDirty,
  setAllOptionalDirty,
  setAllRequiredDirty,
} from "../../redux/systemCheckSlice";
import { Result } from "../Result";
import { GeartestContext } from "../../../micCamCheck/types";
import { useStyles } from "./style";
import { ResultControls } from "../ResultControls";

interface Props {
  context: GeartestContext;
}

const ResultContainer = ({ context }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const meetingState = useMeetingState();
  const { browserSupport, osSupported, osName, browserName } =
    useSelector(selectSystemInfo);
  const audioPermissionState = useSelector(selectAudioPermissionsState);
  const videoPermissionState = useSelector(selectVideoPermissionsState);

  const firebaseResult = useSelector(selectFirebaseResult);
  const firestoreResult = useSelector(selectFirestoreResult);
  const firebaseStorageResult = useSelector(selectFirebaseStorageResult);
  const internetSpeedResult = useSelector(selectInternetSpeedResult);
  const adBlockResult = useSelector(selectAdBlockResult);
  const dailyResult = useSelector(selectDailyResult);
  const vpnResult = useSelector(selectVPNDetectionResult);
  const disableVPNCheck = useSelector(selectDisableVPNCheck);
  const reachabilityResult = useSelector(selectReachabilityResult);
  const systemStatus = useSelector(selectSystemStatus);
  const requiredSystemStatus = useSelector(selectRequiredSystemStatus);
  const isLoading = useSelector(selectIsLoading);
  const isRequiredChecksLoading = useSelector(selectIsRequiredLoading);
  const allChecksCompleted = useSelector(selectHaveChecksBeenRun);
  const { refreshDevices } = useDevices();

  const classes = useStyles();

  const retrySystemCheck = async () => {
    logger.info("[ResultContainer] retry");
    dispatch(
      setAllDirty({
        value: true,
        skipDailyTest: meetingState === "joined-meeting", // skip if user has already joined meeting
      }),
    );
    await refreshDevices();
  };

  useEffect(() => {
    dispatch(setAllRequiredDirty(true));
    trackActionStart(Actions.GEARTEST_SYSCHECK);
  }, [dispatch]);

  useEffect(() => {
    if (isRequiredChecksLoading) return;
    dispatch(setAllOptionalDirty(true));
  }, [dispatch, isRequiredChecksLoading]);

  useEffect(() => {
    if (isLoading || !allChecksCompleted) return;

    trackActionEnd(Actions.GEARTEST_SYSCHECK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allChecksCompleted]);

  return (
    <div id="systemCheck" className={classes.systemCheckContainer}>
      <div className="body">
        <Result
          audioPermissionState={audioPermissionState}
          videoPermissionState={videoPermissionState}
          systemStatus={systemStatus}
          requiredSystemStatus={requiredSystemStatus}
          systemSettings={{
            osSupported,
            browserSupport,
            browserName,
            osName,
          }}
          allChecksCompleted={allChecksCompleted}
          isLoading={isLoading}
          isRequiredChecksLoading={isRequiredChecksLoading}
          isInternetConnectionOK={internetSpeedResult.isSuccessful ?? false}
          isFirebaseAccessible={firebaseResult.isSuccessful ?? false}
          isFirestoreAccessible={firestoreResult.isSuccessful ?? false}
          isFirebaseStorageAccessible={
            firebaseStorageResult.isSuccessful ?? false
          }
          isDailyAccessible={dailyResult.isSuccessful !== false}
          isAdBlockEnabled={adBlockResult.isSuccessful === false}
          isVPNDetected={vpnResult.isSuccessful === false}
          reachabilityResult={reachabilityResult}
          disableVPNCheck={disableVPNCheck}
          context={context}
        />
        <ResultControls
          context={context}
          retrySystemCheck={retrySystemCheck}
          audioPermissionState={audioPermissionState}
          videoPermissionState={videoPermissionState}
        />
      </div>
      <DevicePromptPopup />
    </div>
  );
};

export default ResultContainer;
